import React, { memo, useCallback, useEffect, useRef } from 'react';
/** REDUX */
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  clearNewMessagesAction,
  scrollToBottomAction,
  setHasMoreMessagesAction,
  setHighlightMessageAction,
  setMessagesInStoreAction,
} from '../../../redux/actions/collaboration';
/** APOLLO */
import { useApolloClient } from '@apollo/client';
import getMessagesBefore from '../apollo/getMessagesBefore';
/** COMPONENTS */
import Message from '../Message';
import MessageSubmit from '../MessageSubmit';
import InfiniteScroll from 'react-infinite-scroller';
import Screenshot from '../Screenshot';
/** UTILS */
import moment from 'moment';
import { combineTwoMessageList } from '../utils';
import { HIGHLIGHTED_MESSAGE_DURATION } from '../../../config/constants';
/** CUSTOM UI COMPONENTS */
import * as CollaborationChatUI from './CollaborationChatUI';
import { globalFormat, isDateValid } from '../../../utils/formatting';
import { newMessageButtonWidth } from './utils';

const parseChatDate = (printDate: string) => {
  const { dateFormat } = globalFormat.getGlobalFormat();
  if (!isDateValid(printDate)) {
    return printDate;
  }

  if (moment().isSame(printDate, 'day')) {
    return 'TODAY';
  }

  const yesterday = moment().subtract(1, 'day');

  if (yesterday.isSame(printDate, 'day')) {
    return 'YESTERDAY';
  }

  return globalFormat.formatDate(printDate, { dateFormat });
};

export default memo(function CollaborationChat() {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const { dateFormat } = globalFormat.getGlobalFormat();
  const messagesEndRef = useRef<any>(null);
  let initializeDate = '';

  const isLoading = useSelector((state: any) => state.collaboration.isLoading);
  const messages = useSelector((state: any) => state.collaboration.messages);
  const currentChannelId = useSelector(
    (state: any) => state.collaboration.currentChannelId,
  );
  const scrollToBottom = useSelector(
    (state: any) => state.collaboration.scrollToBottom,
  );
  const messageDecorator = useSelector(
    (state: any) => state.collaboration.decorator,
  );
  const loadedChannelId = useSelector(
    (state: any) => state.collaboration.loadedChannelId,
  );
  const hasNewMessages = useSelector(
    (state: any) => state.collaboration.hasNewMessages,
  );
  const hasMoreMessages = useSelector(
    (state: any) => state.collaboration.hasMoreMessages,
  );
  const highlightMessage = useSelector(
    (state: any) => state.collaboration.highlightMessage,
  );
  const preparedCanvas = useSelector(
    (state: any) => state.collaboration.preparedCanvas,
  );

  const onScrollToBottomWrapper = useCallback(
    (scrollEvent: any) => {
      if (hasNewMessages) {
        const bottom =
          scrollEvent.target.scrollHeight - scrollEvent.target.scrollTop ===
          scrollEvent.target.clientHeight;
        if (bottom) {
          batch(() => {
            dispatch(scrollToBottomAction(true));
            dispatch(clearNewMessagesAction());
          });
        }
      }
    },
    [dispatch, hasNewMessages],
  );

  const clearNewMessagesFlag = useCallback(() => {
    batch(() => {
      dispatch(scrollToBottomAction(true));
      dispatch(clearNewMessagesAction());
    });
  }, [dispatch]);

  const loadOlderMessages = useCallback(async () => {
    /* On scroll to top if there are more messages load 20 */
    const getMessageBeforeRes = await client.query({
      query: getMessagesBefore,
      variables: {
        count: 20,
        channelId: currentChannelId,
        messageId: messages[0].id,
      },
    });

    /* Combine read and unread messages together */
    const oldMessages = getMessageBeforeRes?.data?.messagesBefore?.messages;
    const hasMore = getMessageBeforeRes?.data?.messagesBefore?.hasMore;

    const combinedMessages = combineTwoMessageList(oldMessages, messages);

    batch(() => {
      dispatch(setMessagesInStoreAction(combinedMessages));
      dispatch(setHasMoreMessagesAction(hasMore));
    });
  }, [dispatch, client, currentChannelId, messages]);

  useEffect(() => {
    /* Scroll to latest messages */
    if (scrollToBottom) {
      messagesEndRef?.current?.scrollIntoView({ behavior: 'auto' });
      dispatch(scrollToBottomAction(false));
    }
  }, [dispatch, scrollToBottom]);

  useEffect(() => {
    /* Scroll to highlighted message and unmark it after 6 seconds */
    if (highlightMessage) {
      document
        .getElementById(`message-${highlightMessage}`)
        ?.scrollIntoView({ behavior: 'auto' });
      setTimeout(() => {
        dispatch(setHighlightMessageAction(''));
      }, HIGHLIGHTED_MESSAGE_DURATION);
    }
  }, [dispatch, highlightMessage]);

  const parseDate = (newDate: Date | string) => {
    return globalFormat.formatDate(newDate, {
      dateFormat,
    });
  };

  /* Show the current date label on top of
   each message which has different date than today */
  const DateFlag = (printDate: string, isNewMessage: boolean) => {
    // bad implementation, but not going to touch chat at this moment until next version on Workspace
    initializeDate = parseDate(printDate);

    const parsedDate = parseChatDate(printDate);

    return (
      <CollaborationChatUI.SplitterContainer>
        <CollaborationChatUI.DateSeparate isNewMessage={isNewMessage} />
        <CollaborationChatUI.CreatedDate isNewMessage={isNewMessage}>
          {parsedDate}
        </CollaborationChatUI.CreatedDate>
        <CollaborationChatUI.DateSeparate isNewMessage={isNewMessage} />
      </CollaborationChatUI.SplitterContainer>
    );
  };

  const inputAreaDiv = document.getElementById('message-submit');
  const messagePos: any = {};

  if (inputAreaDiv) {
    const pos: DOMRect | ClientRect = inputAreaDiv.getBoundingClientRect();
    messagePos.top = pos.top - 28;
    /* Place new message button to the middle of textinput area. */
    messagePos.left = pos.left + pos.width / 2 - newMessageButtonWidth / 2;
  }

  return (
    <>
      <CollaborationChatUI.Container>
        <CollaborationChatUI.ChatContent
          id={`chat-container`}
          onScroll={onScrollToBottomWrapper}
        >
          {(!loadedChannelId || isLoading) && (
            <CollaborationChatUI.LoaderContaier active inverted>
              <CollaborationChatUI.StyledLoader size="medium" />
            </CollaborationChatUI.LoaderContaier>
          )}
          {loadedChannelId && messages?.length > 0 && !isLoading && (
            <InfiniteScroll
              pageStart={0}
              loadMore={loadOlderMessages}
              hasMore={hasMoreMessages}
              initialLoad={false}
              loader={<CollaborationChatUI.LazyLoader key={0} />}
              isReverse={true}
              useWindow={false}
            >
              {messages.map((message: any, messageIndex: number) => (
                <CollaborationChatUI.MessageContainer key={message.id}>
                  {/* If new date and new message happens together, using blue date splitter to indicate both */}
                  {initializeDate !== parseDate(message.createdAt)
                    ? DateFlag(
                        message.createdAt,
                        messageDecorator.id === message.id,
                      )
                    : /* Display the new message splitter */
                      messageDecorator.id === message.id && (
                        <CollaborationChatUI.SplitterContainer>
                          <CollaborationChatUI.UnreadSeparate />
                          <CollaborationChatUI.NewMessageFlag>
                            New message
                          </CollaborationChatUI.NewMessageFlag>
                        </CollaborationChatUI.SplitterContainer>
                      )}
                  <CollaborationChatUI.MessageInfo
                    className={
                      messageIndex === messages.length - 1
                        ? 'msg-last-child'
                        : ''
                    }
                  >
                    <Message message={message} />
                  </CollaborationChatUI.MessageInfo>
                </CollaborationChatUI.MessageContainer>
              ))}
              <div ref={messagesEndRef} />
            </InfiniteScroll>
          )}
          {hasNewMessages && (
            <CollaborationChatUI.NewMessageButton
              style={messagePos}
              onClick={clearNewMessagesFlag}
            >
              <CollaborationChatUI.ArrowDownIcon name="arrow alternate circle down outline" />
              <CollaborationChatUI.NewMessageText>
                New Messages
              </CollaborationChatUI.NewMessageText>
            </CollaborationChatUI.NewMessageButton>
          )}
        </CollaborationChatUI.ChatContent>
        <MessageSubmit />
      </CollaborationChatUI.Container>
      {preparedCanvas && <Screenshot />}
    </>
  );
});
